import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	GREEN, IMsiSingleEnvironmentalCredentials, IGreenState,
	IMsiEnvironmentalCredentials, IGreenSummary, IRollingAerValues,
	IEmissionsOfEcaSeaAreas, IEmissionsOfOperationalPhases, IVesselCiiAlignments,
	IFleetCiiAlignment, IVesselsEtsAndFuelEuEmissions,
	IVesselsMainEngineFuelConsumes,
	IVesselKeyMetricsDateBased
} from './greenTypes';
import { RootState } from '../../core/store';

const initialState: IGreenState = {
	msiEnvironmentalCredentialsData: {
		msiEnvironmentalCredentialsDatas: [],
		msiEnvironmentalCredentialsData: null,
		portfolioEnvironmentalCredentials: null,
		loading: false,
		error: null,
	},
	greenSummaryData: {
		greenSummaryData: [],
		loading: false,
		error: null,
	},
	rollingAerValuesData: {
		rollingAerValuesData: [],
		loading: false,
		error: null
	},
	emissionsOfEcaSeaAreasData: {
		emissionsOfEcaSeaAreasData: [],
		loading: false,
		error: null
	},
	cohortEmissionsOfEcaSeaAreasData: {
		cohortEmissionsOfEcaSeaAreasData: [],
		loading: false,
		error: null
	},
	emissionsOfOperationalPhasesData: {
		emissionsOfOperationalPhasesData: [],
		loading: false,
		error: null
	},
	cohortEmissionsOfOperationalPhasesData: {
		cohortEmissionsOfOperationalPhasesData: [],
		loading: false,
		error: null
	},
	vesselCiiAlignmentsData: {
		vesselCiiAlignmentData: [],
		loading: false,
		error: null
	},
	fleetCiiAlignmentData: {
		fleetCiiAlignmentData: null,
		loading: false,
		error: null
	},
	vesselsEtsAndFuelEuEmissionsData: {
		vesselsEtsAndFuelEuEmissionsData: null,
		loading: false,
		error: null
	},
	cohortVesselsEtsAndFuelEuEmissionsData: {
		cohortVesselsEtsAndFuelEuEmissionsData: null,
		loading: false,
		error: null
	},
	vesselsMainEngineFuelConsumesData: {
		vesselsMainEngineFuelConsumesData: null,
		loading: false,
		error: null
	},
	cohortVesselsMainEngineFuelConsumesData: {
		cohortVesselsMainEngineFuelConsumesData: null,
		loading: false,
		error: null
	},
	// vessels key metrics date based
	vesselKeyMetricsDateBasedData: {
		vesselKeyMetricsDateBasedData: null,
		loading: false,
		error: null
	},
	cohortVesselKeyMetricsDateBasedData: {
		cohortVesselKeyMetricsDateBasedData: null,
		loading: false,
		error: null
	}
};

const greenSlice = createSlice({
	name: GREEN,
	initialState,
	reducers: {
		getMsiEnvironmentalCredentialsRequestAction: (state: IGreenState, _: PayloadAction<number[] | undefined>) => {
			state.msiEnvironmentalCredentialsData!.loading = true;
			state.msiEnvironmentalCredentialsData!.error = null;
		},
		getMsiEnvironmentalCredentialsSuccessAction: (
			state: IGreenState,
			{ payload: data }: PayloadAction<IMsiEnvironmentalCredentials>,
		) => {
			if (data.singleEnvironmentalCredentials && data.singleEnvironmentalCredentials.length === 1) {
				state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsData = data.singleEnvironmentalCredentials[0];
				// if msiEnvironmentalCredentialsDatas does not contain data then add data to the msiEnvironmentalCredentialsDatas
				if (!state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsDatas) {
					state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsDatas = data.singleEnvironmentalCredentials;
				} else {
					// add data to the msiEnvironmentalCredentialsDatas if it doesnt exist
					if (!state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsDatas.find((d: IMsiSingleEnvironmentalCredentials) => d.imoNumber === data.singleEnvironmentalCredentials![0].imoNumber)) {
						state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsDatas.push(data.singleEnvironmentalCredentials[0]);
					}
				}
			} else {
				state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsDatas = data.singleEnvironmentalCredentials;
				state.msiEnvironmentalCredentialsData!.portfolioEnvironmentalCredentials = data.portfolioEnvironmentalCredential;
			}

			state.msiEnvironmentalCredentialsData!.loading = false;
			state.msiEnvironmentalCredentialsData!.error = null;
		},
		selectSingleMsiEnvironmentalCredentials: (state: IGreenState, action: PayloadAction<IMsiSingleEnvironmentalCredentials>) => {
			state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsData = action.payload;
			state.msiEnvironmentalCredentialsData!.loading = false;
			state.msiEnvironmentalCredentialsData!.error = null;
		},
		getMsiEnvironmentalCredentialsErrorAction: (
			state: IGreenState,
			{ payload: error }: PayloadAction<any>,
		) => {
			state.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsData = null;
			state.msiEnvironmentalCredentialsData!.loading = false;
			state.msiEnvironmentalCredentialsData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getGreenSummaryRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.greenSummaryData!.loading = true;
			state.greenSummaryData!.error = null;
		},
		getGreenSummarySuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IGreenSummary[]>) => {
			state.greenSummaryData!.greenSummaryData = data;
			state.greenSummaryData!.loading = false;
			state.greenSummaryData!.error = null;
		},
		getGreenSummaryErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.greenSummaryData!.loading = false;
			state.greenSummaryData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getRollingAerValuesRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumber: number, startDate: string, endDate: string }>) => {
			state.rollingAerValuesData!.loading = true;
			state.rollingAerValuesData!.error = null;
		},
		getRollingAerValuesSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IRollingAerValues[]>) => {
			state.rollingAerValuesData!.rollingAerValuesData = data;
			state.rollingAerValuesData!.loading = false;
			state.rollingAerValuesData!.error = null;
		},
		getRollingAerValuesErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.rollingAerValuesData!.loading = false;
			state.rollingAerValuesData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getEmissionsOfEcaSeaAreasRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
			state.emissionsOfEcaSeaAreasData!.loading = true;
			state.emissionsOfEcaSeaAreasData!.error = null;
		},
		getEmissionsOfEcaSeaAreasSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IEmissionsOfEcaSeaAreas[]>) => {
			state.emissionsOfEcaSeaAreasData!.emissionsOfEcaSeaAreasData = data;
			state.emissionsOfEcaSeaAreasData!.loading = false;
			state.emissionsOfEcaSeaAreasData!.error = null;
		},
		getEmissionsOfEcaSeaAreasErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.emissionsOfEcaSeaAreasData!.loading = false;
			state.emissionsOfEcaSeaAreasData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getCohortEmissionsOfEcaSeaAreasRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.cohortEmissionsOfEcaSeaAreasData!.loading = true;
			state.cohortEmissionsOfEcaSeaAreasData!.error = null;
		},
		getCohortEmissionsOfEcaSeaAreasSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IEmissionsOfEcaSeaAreas[]>) => {
			state.cohortEmissionsOfEcaSeaAreasData!.cohortEmissionsOfEcaSeaAreasData = data;
			state.cohortEmissionsOfEcaSeaAreasData!.loading = false;
			state.cohortEmissionsOfEcaSeaAreasData!.error = null;
		},
		getCohortEmissionsOfEcaSeaAreasErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.cohortEmissionsOfEcaSeaAreasData!.loading = false;
			state.cohortEmissionsOfEcaSeaAreasData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getEmissionsOfOperationalPhasesRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
			state.emissionsOfOperationalPhasesData!.loading = true;
			state.emissionsOfOperationalPhasesData!.error = null;
		},
		getEmissionsOfOperationalPhasesSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IEmissionsOfOperationalPhases[]>) => {
			state.emissionsOfOperationalPhasesData!.emissionsOfOperationalPhasesData = data;
			state.emissionsOfOperationalPhasesData!.loading = false;
			state.emissionsOfOperationalPhasesData!.error = null;
		},
		getEmissionsOfOperationalPhasesErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.emissionsOfOperationalPhasesData!.loading = false;
			state.emissionsOfOperationalPhasesData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getCohortEmissionsOfOperationalPhasesRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.cohortEmissionsOfOperationalPhasesData!.loading = true;
			state.cohortEmissionsOfOperationalPhasesData!.error = null;
		},
		getCohortEmissionsOfOperationalPhasesSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IEmissionsOfOperationalPhases[]>) => {
			state.cohortEmissionsOfOperationalPhasesData!.cohortEmissionsOfOperationalPhasesData = data;
			state.cohortEmissionsOfOperationalPhasesData!.loading = false;
			state.cohortEmissionsOfOperationalPhasesData!.error = null;
		},
		getCohortEmissionsOfOperationalPhasesErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.cohortEmissionsOfOperationalPhasesData!.loading = false;
			state.cohortEmissionsOfOperationalPhasesData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getVesselCiiAlignmentsRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumber: number, startYearMonth: number, endYearMonth: number }>) => {
			state.vesselCiiAlignmentsData!.loading = true;
			state.vesselCiiAlignmentsData!.error = null;
		},
		getVesselCiiAlignmentsSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IVesselCiiAlignments[]>) => {
			state.vesselCiiAlignmentsData!.vesselCiiAlignmentData = data;
			state.vesselCiiAlignmentsData!.loading = false;
			state.vesselCiiAlignmentsData!.error = null;
		},
		getVesselCiiAlignmentsErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.vesselCiiAlignmentsData!.loading = false;
			state.vesselCiiAlignmentsData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getFleetCiiAlignmentRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.fleetCiiAlignmentData!.loading = true;
			state.fleetCiiAlignmentData!.error = null;
		},
		getFleetCiiAlignmentSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IFleetCiiAlignment>) => {
			state.fleetCiiAlignmentData!.fleetCiiAlignmentData = data;
			state.fleetCiiAlignmentData!.loading = false;
			state.fleetCiiAlignmentData!.error = null;
		},
		getFleetCiiAlignmentPhasesErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.fleetCiiAlignmentData!.loading = false;
			state.fleetCiiAlignmentData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getVesselsEtsAndFuelEuEmissionsRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.vesselsEtsAndFuelEuEmissionsData!.loading = true;
			state.vesselsEtsAndFuelEuEmissionsData!.error = null;
		},
		getVesselsEtsAndFuelEuEmissionsSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IVesselsEtsAndFuelEuEmissions[]>) => {
			state.vesselsEtsAndFuelEuEmissionsData!.vesselsEtsAndFuelEuEmissionsData = data;
			state.vesselsEtsAndFuelEuEmissionsData!.loading = false;
			state.vesselsEtsAndFuelEuEmissionsData!.error = null;
		},
		getVesselsEtsAndFuelEuEmissionsErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.vesselsEtsAndFuelEuEmissionsData!.loading = false;
			state.vesselsEtsAndFuelEuEmissionsData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getCohortVesselsEtsAndFuelEuEmissionsRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.cohortVesselsEtsAndFuelEuEmissionsData!.loading = true;
			state.cohortVesselsEtsAndFuelEuEmissionsData!.error = null;
		},
		getCohortVesselsEtsAndFuelEuEmissionsSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IVesselsEtsAndFuelEuEmissions[]>) => {
			state.cohortVesselsEtsAndFuelEuEmissionsData!.cohortVesselsEtsAndFuelEuEmissionsData = data;
			state.cohortVesselsEtsAndFuelEuEmissionsData!.loading = false;
			state.cohortVesselsEtsAndFuelEuEmissionsData!.error = null;
		},
		getCohortVesselsEtsAndFuelEuEmissionsErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.cohortVesselsEtsAndFuelEuEmissionsData!.loading = false;
			state.cohortVesselsEtsAndFuelEuEmissionsData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			}
		},

		getVesselsMainEngineFuelConsumesRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.vesselsMainEngineFuelConsumesData!.loading = true;
			state.vesselsMainEngineFuelConsumesData!.error = null;
		},
		getVesselsMainEngineFuelConsumesSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IVesselsMainEngineFuelConsumes[]>) => {
			state.vesselsMainEngineFuelConsumesData!.vesselsMainEngineFuelConsumesData = data;
			state.vesselsMainEngineFuelConsumesData!.loading = false;
			state.vesselsMainEngineFuelConsumesData!.error = null;
		},
		getVesselsMainEngineFuelConsumesErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.vesselsMainEngineFuelConsumesData!.loading = false;
			state.vesselsMainEngineFuelConsumesData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getCohortVesselsMainEngineFuelConsumesRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.cohortVesselsMainEngineFuelConsumesData!.loading = true;
			state.cohortVesselsMainEngineFuelConsumesData!.error = null;
		},
		getCohortVesselsMainEngineFuelConsumesSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IVesselsMainEngineFuelConsumes[]>) => {
			state.cohortVesselsMainEngineFuelConsumesData!.cohortVesselsMainEngineFuelConsumesData = data;
			state.cohortVesselsMainEngineFuelConsumesData!.loading = false;
			state.cohortVesselsMainEngineFuelConsumesData!.error = null;
		},
		getCohortVesselsMainEngineFuelConsumesErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.cohortVesselsMainEngineFuelConsumesData!.loading = false;
			state.cohortVesselsMainEngineFuelConsumesData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		// vessels key metrics date based
		getVesselKeyMetricsDateBasedRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.vesselKeyMetricsDateBasedData!.loading = true;
			state.vesselKeyMetricsDateBasedData!.error = null;
		},
		getVesselKeyMetricsDateBasedSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IVesselKeyMetricsDateBased[]>) => {
			state.vesselKeyMetricsDateBasedData!.vesselKeyMetricsDateBasedData = data;
			state.vesselKeyMetricsDateBasedData!.loading = false;
			state.vesselKeyMetricsDateBasedData!.error = null;
		},
		getVesselKeyMetricsDateBasedErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.vesselKeyMetricsDateBasedData!.loading = false;
			state.vesselKeyMetricsDateBasedData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},

		getCohortVesselKeyMetricsDateBasedRequestAction: (state: IGreenState, _: PayloadAction<{ imoNumbers: number[], startYearMonth: number, endYearMonth: number }>) => {
			state.cohortVesselKeyMetricsDateBasedData!.loading = true;
			state.cohortVesselKeyMetricsDateBasedData!.error = null;
			state.cohortVesselKeyMetricsDateBasedData!.cohortVesselKeyMetricsDateBasedData = null;
		},
		getCohortVesselKeyMetricsDateBasedSuccessAction: (state: IGreenState, { payload: data }: PayloadAction<IVesselKeyMetricsDateBased[]>) => {
			state.cohortVesselKeyMetricsDateBasedData!.cohortVesselKeyMetricsDateBasedData = data;
			state.cohortVesselKeyMetricsDateBasedData!.loading = false;
			state.cohortVesselKeyMetricsDateBasedData!.error = null;
		},
		getCohortVesselKeyMetricsDateBasedErrorAction: (state: IGreenState, { payload: error }: PayloadAction<any>) => {
			state.cohortVesselKeyMetricsDateBasedData!.loading = false;
			state.cohortVesselKeyMetricsDateBasedData!.error = {
				message: (error.response && error.response.data) || error.message || '',
				name: error.name,
				stack: error.stack,
			};
		},
	},
});

export const greenActions = {
	...greenSlice.actions
};

export default greenSlice.reducer;

export const selectMsiEnvironmentalCredentialsDatas = (state: RootState) => state.greenSlice.msiEnvironmentalCredentialsData!.msiEnvironmentalCredentialsDatas;
export const selectEmissionsOfEcaSeaAreas = (state: RootState) => state.greenSlice.emissionsOfEcaSeaAreasData!;
export const selectCohortEmissionsOfEcaSeaAreas = (state: RootState) => state.greenSlice.cohortEmissionsOfEcaSeaAreasData!;
export const selectEmissionsOfOperationalPhases = (state: RootState) => state.greenSlice.emissionsOfOperationalPhasesData!;
export const selectCohortEmissionsOfOperationalPhases = (state: RootState) => state.greenSlice.cohortEmissionsOfOperationalPhasesData!;
export const selectVesselCiiAlignments = (state: RootState) => state.greenSlice.vesselCiiAlignmentsData!;
export const selectFleetCiiAlignment = (state: RootState) => state.greenSlice.fleetCiiAlignmentData!;
export const selectGreenSummaryData = (state: RootState) => state.greenSlice.greenSummaryData!;
export const selectVesselsEtsAndFuelEuEmissions = (state: RootState) => state.greenSlice.vesselsEtsAndFuelEuEmissionsData!;
export const selectCohortVesselsEtsAndFuelEuEmissions = (state: RootState) => state.greenSlice.cohortVesselsEtsAndFuelEuEmissionsData!;
export const selectVesselsMainEngineFuelConsumes = (state: RootState) => state.greenSlice.vesselsMainEngineFuelConsumesData!;
export const selectCohortVesselsMainEngineFuelConsumes = (state: RootState) => state.greenSlice.cohortVesselsMainEngineFuelConsumesData!;
export const selectVesselKeyMetricsDateBased = (state: RootState) => state.greenSlice.vesselKeyMetricsDateBasedData!;
export const selectCohortVesselKeyMetricsDateBased = (state: RootState) => state.greenSlice.cohortVesselKeyMetricsDateBasedData!;